import React, { useState, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import { crearEntradaExpediente, actualizarEntradaExpediente, eliminarEntradaExpediente } from "../../services/api";

function parseTimestamp(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts._seconds) return new Date(ts._seconds * 1000);
  return new Date(ts);
}

function esEditable(createdAt) {
  const fecha = parseTimestamp(createdAt);
  if (!fecha || isNaN(fecha.getTime())) return false;
  const ahora = new Date();
  const hace24h = new Date(ahora - 24 * 60 * 60 * 1000);
  return fecha > hace24h;
}

function tiempoRelativo(createdAt) {
  const fecha = parseTimestamp(createdAt);
  if (!fecha || isNaN(fecha.getTime())) return '';
  const ahora = new Date();
  const diffMs = ahora - fecha;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHoras = Math.floor(diffMin / 60);
  if (diffMin < 1) return 'hace un momento';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHoras < 24) return `hace ${diffHoras}h`;
  return '';
}

export default function ControlPeso({ pesoChartData, ultimoPeso, mascotaInfo, objetoId, onPesoRegistered, onPesoUpdated, onPesoDeleted }) {
  const [pesoSectionOpen, setPesoSectionOpen] = useState(false);
  const [showPesoForm, setShowPesoForm] = useState(false);
  const [pesoValue, setPesoValue] = useState('');
  const [fechaValue, setFechaValue] = useState(() => new Date().toISOString().split('T')[0]);
  const [guardandoPeso, setGuardandoPeso] = useState(false);

  // Edit/delete state
  const [editingId, setEditingId] = useState(null);
  const [editPesoValue, setEditPesoValue] = useState('');
  const [guardandoEdit, setGuardandoEdit] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [eliminandoId, setEliminandoId] = useState(null);

  // Records sorted most recent first for the list
  const registrosOrdenados = useMemo(() => {
    return [...pesoChartData].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [pesoChartData]);

  // === CREATE handler ===
  async function handleGuardarPeso(e) {
    e.preventDefault();
    const peso = parseFloat(pesoValue);
    if (!peso || peso <= 0 || peso > 500) {
      toast.error('Ingresa un peso válido');
      return;
    }
    if (!fechaValue) {
      toast.error('Selecciona una fecha');
      return;
    }

    setGuardandoPeso(true);
    try {
      const entryData = {
        fecha: fechaValue,
        tipo: 'registro_peso',
        peso: peso,
        fuente: 'manual',
        notas: 'Peso registrado manualmente'
      };

      const resp = await crearEntradaExpediente(objetoId, entryData);
      if (resp.error) {
        toast.error(resp.error);
        return;
      }

      toast.success('Peso registrado correctamente');
      setShowPesoForm(false);
      setPesoValue('');
      setFechaValue(new Date().toISOString().split('T')[0]);

      if (onPesoRegistered && resp.data) {
        onPesoRegistered(resp.data);
      }
    } catch (error) {
      console.error('Error al registrar peso:', error);
      toast.error('Error al registrar el peso');
    } finally {
      setGuardandoPeso(false);
    }
  }

  function handleCancelarPeso() {
    setShowPesoForm(false);
    setPesoValue('');
    setFechaValue(new Date().toISOString().split('T')[0]);
  }

  // === EDIT handlers ===
  function handleStartEdit(record) {
    setEditingId(record.id);
    setEditPesoValue(String(record.peso));
    setConfirmDeleteId(null);
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditPesoValue('');
  }

  async function handleGuardarEdit(record) {
    const peso = parseFloat(editPesoValue);
    if (!peso || peso <= 0 || peso > 500) {
      toast.error('Ingresa un peso válido');
      return;
    }
    setGuardandoEdit(true);
    try {
      const resp = await actualizarEntradaExpediente(objetoId, record.id, { peso });
      if (resp.error) {
        toast.error(resp.error);
        return;
      }
      toast.success('Peso actualizado');
      setEditingId(null);
      setEditPesoValue('');
      if (onPesoUpdated) onPesoUpdated(record.id, peso);
    } catch (error) {
      console.error('Error al actualizar peso:', error);
      toast.error('Error al actualizar el peso');
    } finally {
      setGuardandoEdit(false);
    }
  }

  // === DELETE handlers ===
  async function handleEliminar(record) {
    setEliminandoId(record.id);
    try {
      const resp = await eliminarEntradaExpediente(objetoId, record.id);
      if (resp.error) {
        toast.error(resp.error);
        return;
      }
      toast.success('Registro de peso eliminado');
      setConfirmDeleteId(null);
      if (onPesoDeleted) onPesoDeleted(record.id);
    } catch (error) {
      console.error('Error al eliminar peso:', error);
      toast.error('Error al eliminar el registro');
    } finally {
      setEliminandoId(null);
    }
  }

  return (
    <div
      className="expediente-card"
      style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}
    >
      {/* Header del card */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 16px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div
          onClick={() => setPesoSectionOpen(!pesoSectionOpen)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}
        >
          <span style={{ fontSize: '18px' }}>📊</span>
          <span style={{ fontWeight: '600', fontSize: '15px', color: '#1f2937' }}>
            Control de Peso
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {ultimoPeso && (
            <span style={{
              backgroundColor: '#dbeafe',
              color: '#1e40af',
              fontSize: '13px',
              padding: '4px 10px',
              borderRadius: '20px',
              fontWeight: '600'
            }}>
              {ultimoPeso} kg
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!pesoSectionOpen) setPesoSectionOpen(true);
              setShowPesoForm(true);
              setEditingId(null);
              setConfirmDeleteId(null);
            }}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#84cc16',
              color: '#fff',
              fontSize: '16px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              lineHeight: 1,
              padding: 0,
              flexShrink: 0
            }}
            title="Registrar peso"
          >
            +
          </button>
          <i
            onClick={() => setPesoSectionOpen(!pesoSectionOpen)}
            className={`bi bi-chevron-${pesoSectionOpen ? 'down' : 'right'}`}
            style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer' }}
          ></i>
        </div>
      </div>

      {/* Contenido colapsable */}
      {pesoSectionOpen && (
        <div style={{ padding: 'var(--space-3)' }}>
          {/* Formulario inline para registrar peso */}
          {showPesoForm && (
            <div style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: 'var(--space-3)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>
                  Registrar peso
                </span>
                <button
                  onClick={handleCancelarPeso}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '0 4px',
                    lineHeight: 1
                  }}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>

              <form onSubmit={handleGuardarPeso}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      color: '#6b7280',
                      marginBottom: '4px',
                      fontWeight: '500'
                    }}>
                      Peso (kg)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max="500"
                      value={pesoValue}
                      onChange={(e) => setPesoValue(e.target.value)}
                      placeholder="Ej: 5.2"
                      required
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      color: '#6b7280',
                      marginBottom: '4px',
                      fontWeight: '500'
                    }}>
                      Fecha
                    </label>
                    <input
                      type="date"
                      value={fechaValue}
                      onChange={(e) => setFechaValue(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      required
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={handleCancelarPeso}
                    disabled={guardandoPeso}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      color: '#374151',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={guardandoPeso}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: '#84cc16',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: guardandoPeso ? 'not-allowed' : 'pointer',
                      opacity: guardandoPeso ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {guardandoPeso && (
                      <div className="spinner-border spinner-border-sm" role="status"
                        style={{ width: '14px', height: '14px', borderWidth: '2px' }}></div>
                    )}
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          )}

          {pesoChartData.length > 1 ? (
            <div style={{ height: '150px', marginBottom: 'var(--space-2)' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pesoChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="fechaFormateada"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    domain={['dataMin - 0.5', 'dataMax + 0.5']}
                    unit=" kg"
                  />
                  <Tooltip
                    formatter={(value) => [`${value} kg`, 'Peso']}
                    labelFormatter={(label) => `Fecha: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="peso"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: '#1d4ed8' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : pesoChartData.length === 1 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-3)', color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}>
              Solo hay un registro de peso. Se necesitan al menos 2 para mostrar la grafica.
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--space-3)', color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}>
              No hay registros de peso aun.
            </div>
          )}

          {/* Lista de registros de peso */}
          {registrosOrdenados.length > 0 && (
            <div style={{ marginTop: 'var(--space-2)' }}>
              {registrosOrdenados.map((record) => {
                const editable = esEditable(record.createdAt);
                const tiempo = tiempoRelativo(record.createdAt);
                const fechaDisplay = new Date(record.fecha).toLocaleDateString('es-MX', {
                  day: 'numeric', month: 'short', year: 'numeric'
                });
                const isEditing = editingId === record.id;
                const isConfirmingDelete = confirmDeleteId === record.id;

                // Inline edit mode
                if (isEditing) {
                  return (
                    <div
                      key={record.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        marginBottom: '6px'
                      }}
                    >
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="500"
                        value={editPesoValue}
                        onChange={(e) => setEditPesoValue(e.target.value)}
                        autoFocus
                        style={{
                          width: '80px',
                          padding: '4px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px',
                          outline: 'none'
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleGuardarEdit(record); }
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                      />
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>kg</span>
                      <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '4px' }}>
                        {fechaDisplay}
                      </span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                        <button
                          onClick={() => handleGuardarEdit(record)}
                          disabled={guardandoEdit}
                          style={{
                            padding: '3px 10px',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: '#4CAF50',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: guardandoEdit ? 'not-allowed' : 'pointer',
                            opacity: guardandoEdit ? 0.7 : 1
                          }}
                        >
                          {guardandoEdit ? '...' : 'OK'}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={guardandoEdit}
                          style={{
                            padding: '3px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: '#fff',
                            color: '#6b7280',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    </div>
                  );
                }

                // Confirm delete mode
                if (isConfirmingDelete) {
                  return (
                    <div
                      key={record.id}
                      style={{
                        padding: '10px 12px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        marginBottom: '6px'
                      }}
                    >
                      <div style={{ fontSize: '13px', color: '#991b1b', marginBottom: '8px' }}>
                        ¿Eliminar este registro de peso?
                      </div>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={eliminandoId === record.id}
                          style={{
                            padding: '4px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: '#fff',
                            color: '#374151',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={() => handleEliminar(record)}
                          disabled={eliminandoId === record.id}
                          style={{
                            padding: '4px 12px',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: '#dc2626',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: eliminandoId === record.id ? 'not-allowed' : 'pointer',
                            opacity: eliminandoId === record.id ? 0.7 : 1
                          }}
                        >
                          {eliminandoId === record.id ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      </div>
                    </div>
                  );
                }

                // Normal display
                return (
                  <div
                    key={record.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderBottom: '1px solid #f3f4f6',
                      gap: '8px'
                    }}
                  >
                    <span style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>
                      {record.peso} kg
                    </span>
                    <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {fechaDisplay}
                    </span>
                    {tiempo && (
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                        ({tiempo})
                      </span>
                    )}
                    {editable && (
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
                        <button
                          onClick={() => handleStartEdit(record)}
                          title="Editar"
                          style={{
                            width: '28px',
                            height: '28px',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                            color: '#6b7280',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <i className="bi bi-pencil" style={{ fontSize: '13px' }}></i>
                        </button>
                        <button
                          onClick={() => { setConfirmDeleteId(record.id); setEditingId(null); }}
                          title="Eliminar"
                          style={{
                            width: '28px',
                            height: '28px',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                            color: '#6b7280',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
                        >
                          <i className="bi bi-trash3" style={{ fontSize: '13px' }}></i>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginTop: 'var(--space-2)' }}>
            <div>
              <strong>Esterilizado:</strong> {mascotaInfo?.esterilizado ? 'Si' : 'No'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
